import { Component, NgZone, OnInit } from '@angular/core';
import { Select } from '@ngxs/store';
import { ToastService } from '@services/common/toast.service';
import { UserHttpService } from '@services/datasources/user-http.service';
import { OrganizationState } from '@state/organization.state';
import { UserInfoDto } from 'app/@types/dto/user-info-dto.model';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { combineLatest, Observable } from 'rxjs';
import { SecurityRoleDto } from 'app/@types/dto/security-role-dto.model';
import { UserState } from '@state/user.state';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { OrganizationPricingPlanDto } from 'app/@types/dto/organization-pricing-plan-dto.model';

@Component({
    selector: 'app-settings-form-users-managment',
    templateUrl: './settings-form-users-managment.component.html',
    styleUrls: ['./settings-form-users-managment.component.scss'],
})
export class SettingsFormUsersManagmentComponent implements OnInit {
    @Select(OrganizationState.getOrganization) organization$: Observable<OrganizationDto>;
    @Select(OrganizationState.getOrganizationPricingPlan) pricingPlan$: Observable<OrganizationPricingPlanDto | undefined>;
    @Select(UserState.getCurrentUserClaims) userClaims$!: Observable<UserClaims>;

    selectedUser: UserInfoDto | null = null;
    addingEditingMode: boolean = false;
    constructor(
        public toastService: ToastService,
        private readonly ngZone: NgZone,
        private readonly userService: UserHttpService,
    ) {}
    usersList: UserInfoDto[] = [];
    orgId: string | null = null;
    canAdd = false;
    public pricingPlan: OrganizationPricingPlanDto;
    public isLoading: boolean = true;
    public isOrgOwner: boolean = false;

    ngOnInit() {
        this.organization$.subscribe((org) => {
            if (org?.uid) {
                this.orgId = org.uid || null;
                combineLatest([
                    this.userService.getUserByOrganization(org.uid),
                    this.pricingPlan$,
                    this.userClaims$,
                ]).subscribe(([users, pricingPlan, claims]) => {
                    this.usersList = users;
                    this.isOrgOwner = claims.tasks.some(
                        (task) => task.name === 'orguser' && task.organization_id === org.uid,
                    );
                    if (pricingPlan) {
                        this.pricingPlan = pricingPlan;
                        this.canAdd = +pricingPlan.licenseDoc.max_users > users.length && this.isOrgOwner;
                        this.isLoading = false;
                    }
                });
            }
        });
    }

    getRoleNameLabel(roles: SecurityRoleDto[]) {
        const role = roles.find((r) => r.organization_uid === this.orgId);
        if (role) {
            if (role.role_name === 'main_user') return 'Owner';
            return 'User';
        }

        return '--';
    }

    getRoleLastConnexion(roles: SecurityRoleDto[]) {
        const role = roles.find((r) => r.organization_uid === this.orgId);
        if (role?.last_connected) {
            const date = new Date(role.last_connected);
            const formatter = new Intl.DateTimeFormat('fr-FR');

            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const seconds = date.getSeconds().toString().padStart(2, '0');

            return `${formatter.format(new Date(role.last_connected))}  ${hours}:${minutes}:${seconds}`;
        }

        return '--';
    }

    addEditUser(user: any | null) {
        this.selectedUser = user;
        this.addingEditingMode = true;
    }

    oncloseEditUser(event?: { translateKey: string; type: string }) {
        this.isLoading = true;
        this.addingEditingMode = false;
        if (event) {
            this.ngZone.run(() => {
                if (event.type === 'success') {
                    this.toastService.showSuccess({
                        details: event.translateKey,
                    });
                } else {
                    this.toastService.showError({
                        details: event.translateKey,
                    });
                }
                if (this.orgId) {
                    this.userService.getUserByOrganization(this.orgId).subscribe((users) => {
                        this.usersList = users;
                        this.canAdd = +this.pricingPlan.licenseDoc.max_users > users.length;
                        this.isLoading = false;
                    });
                }
            });
        }
        this.isLoading = false;
    }
}
